import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  TextField,
  Paper,
  Typography,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../contexts/AuthContext';
import { useAttributeCategories, useCategoryValues } from '../../../contexts/AttributesContext';
import ColumnDefinition from './ColumnDefinition';
import ValueColumnBuilder from './ValueColumnBuilder';
import { normalizeMetricUpdateBehaviors } from '../../../utils/metricUpdateBehaviors';
import TableSchemaPreview from './TableSchemaPreview';
import { formatText } from '../../common/TextFormatterProvider';
import { tableCreationNamesError, metricNamesForEditing } from '../../../utils/tableCreationNames';
import { tableNameError } from '../../../utils/identifierNames';
import { authenticatedFetch } from '../../../services/api_utils';
import { functionEndpoint } from '../../../services/endpoints';
import { browseExternalSource } from '../../../services/externalSources';
import { externalConnectionLocation, externalSchemaSignature, sourceReferenceKey, sourceReferenceLabel } from '../../../utils/externalSourceReferences';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { getPeriodOptions, DEFAULT_PERIOD, PERIOD_CONFIG } from '../../../utils/periodUtils';
import { classificationError } from '../../../utils/tableSettings';

// The schema first, then which of its columns every upload must supply (Spec 6
// section 2.2), then the classes of its Text detail columns (Spec 2 section 4.4).
const STANDARD_STEPS = [
  'Basic Information',
  'Column Definition',
  'Metrics Columns',
  'Mandatory Columns',
  'Classes',
  'Review'
];
const RAW_STEPS = [
  'Basic Information',
  'Columns',
  'Mandatory Columns',
  'Classes',
  'Review'
];
const EXTERNAL_STEPS = [
  'Basic Information',
  'Source',
  'Columns',
  'Review'
];

const DECODE_TYPES = ['Text', 'Number', 'Date', 'Yes/No'];

// A source column may be mapped as the type it really is, or as Text. Nothing else.
// Text is CAST(col AS STRING), which succeeds on every row in every dialect and is
// used for the column's values AND its filters, so the two always agree — it is how
// you stop a numeric policy reference being summed. The other direction cannot work:
// CAST(a text column AS NUMERIC) fails on the first non-numeric row anywhere in the
// source, so a filter would have to compare as text, and a user seeing 123 and
// filtering for 123 would match nothing against a stored "00123". create_table
// refuses those combinations; this is the same rule, before it can be typed.
const decodeTypesFor = (proposedType) => DECODE_TYPES.filter(
  (type) => type === 'Text' || type === proposedType,
);

// The backend's sentence for a merge table without metrics (create_table.py).
const NO_METRICS_MESSAGE = 'Define at least one metric column. Metrics are the values this table tracks and updates from each upload.';
const RAW_COLUMNS_MESSAGE = 'Raw tables require at least two data columns.';

// create_table answers a refusal the person can act on with a sentence written for
// them, as plain text, with one of these statuses (cloud_functions/tables/create_table.py).
// Its 500 says TABLE_NOT_CREATED_MESSAGE, which is what this form shows for every
// other answer (a gateway's error page) and for a request that never reached it.
const CREATE_TABLE_REFUSAL_STATUSES = [400, 401, 403, 409, 503];
const TABLE_NOT_CREATED_MESSAGE = 'The table could not be created. Try again, and contact support if it happens again.';

// The sentence for a create_table answer that is not ok: the endpoint's own
// refusal, or TABLE_NOT_CREATED_MESSAGE for any other status or for a body that
// is empty or markup, in which case the answer is logged.
async function createTableRefusalMessage(response) {
  const body = await response.text();
  const isRefusal = CREATE_TABLE_REFUSAL_STATUSES.includes(response.status)
    && body.trim() !== ''
    && !body.trimStart().startsWith('<');
  if (isRefusal) return body;
  console.error(`create_table answered ${response.status}:`, body);
  return TABLE_NOT_CREATED_MESSAGE;
}

// create_table.py's sentence for a time-series table with details and none mandatory.
const MANDATORY_DETAIL_MESSAGE = 'At least one detail column must be mandatory, per file or per unit. A detail '
  + 'mandatory per file must have a value for each identifier somewhere in the file; one mandatory per unit '
  + 'must have a value for each identifier in every unit of the file that names it.';

// What the two boxes of the Mandatory Columns step mean, per table type: grouped
// completeness on a time-series table, a value on every row of a raw one.
const TIME_SERIES_MANDATORY_EXPLANATION = 'Choose what every upload must supply. Mandatory per file: for each '
  + 'identifier a file names, at least one of the file\'s units (each table Decode reads from it: a sheet, a '
  + 'region of a sheet or a document\'s records) must give the value, so a payments sheet can leave a detail to '
  + 'the file\'s policy sheet. Mandatory per unit: every unit that names an identifier must give the value '
  + 'itself. A mandatory metric must be given for each identifier and reporting period. Both boxes may be '
  + 'checked. Values already in the table never fill in a missing one, and neither box asks every row to carry '
  + 'the value. A table with detail columns needs at least one of them mandatory.';
const RAW_MANDATORY_EXPLANATION = 'A raw table keeps each row as it is, on its own, so either box means every '
  + 'row must give the value: rows are never combined, within a unit or across the units of a file, to fill '
  + 'one in. Both boxes may be checked; on a raw table they mean the same.';

// Both scoped flags are explicit on every detail column and metric leaf: an
// unticked box is false (Spec 6 section 2.2).
const withScopedFlags = (definition) => ({
  ...definition,
  mandatory_per_file: definition.mandatory_per_file === true,
  mandatory_per_unit: definition.mandatory_per_unit === true,
});

const initialTableData = {
  name: '',
  description: '',
  tableType: 'timeSeries',
  timeSeriesConfig: { period: 'month' },
  identifier: {
    name: '',
    type: 'Text'
  },
  attributeColumns: [],
  tableAttributeColumns: [],
  detailColumns: [],
  // Metrics are always on for a merge table (M196); the schema starts empty
  // and the Metrics step requires at least one. Raw tables carry null.
  metricColumns: { schema: {} },
  ingestion_rules: []
};

const freshInitialTableData = (overrides = {}) => ({
  ...initialTableData,
  timeSeriesConfig: { ...initialTableData.timeSeriesConfig },
  identifier: { ...initialTableData.identifier },
  attributeColumns: [],
  tableAttributeColumns: [],
  detailColumns: [],
  metricColumns: { schema: {} },
  ingestion_rules: [],
  ...overrides,
});

// `initialDraft` is a draft this form reported through `onDraftChange` in an
// earlier mount — the Data page keeps it while the person is away creating a
// source connection (the Source step's Manage), so the wizard resumes where
// it was rather than starting blank.
function CreateTableForm({
  onCancel,
  onTableCreated,
  onManageConnections,
  onSubmittingChange,
  initialDraft = null,
  onDraftChange = null,
}) {
  const [activeStep, setActiveStep] = useState(() => initialDraft?.activeStep ?? 0);
  const [tableData, setTableData] = useState(() => {
    const initial = initialDraft?.tableData || freshInitialTableData();
    if (!initial.metricColumns) return initial;
    return {
      ...initial,
      metricColumns: {
        ...initial.metricColumns,
        schema: normalizeMetricUpdateBehaviors(initial.metricColumns.schema),
      },
    };
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The create request cannot be cancelled once sent, so the dialog around this form
  // must not dismiss while it is in flight; the form reports that state up.
  useEffect(() => {
    if (onSubmittingChange) onSubmittingChange(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);
  const { companyName } = useAuth();
  // Categories, live (AttributesProvider), for the attribute column steps. Values
  // are requested only for the table-level attribute columns this table has
  // taken — the only place ColumnDefinition offers a value picker — never for
  // every category the company has.
  const { categories: attributeCategories } = useAttributeCategories();
  const usedAttributeCategoryIds = useMemo(
    () => (tableData.tableAttributeColumns || []).map((column) => column?.name).filter(Boolean),
    [tableData.tableAttributeColumns],
  );
  const categoryValues = useCategoryValues(usedAttributeCategoryIds);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(() => initialDraft?.selectedTemplateId || '');
  const [storageMode, setStorageMode] = useState(() => initialDraft?.storageMode || 'native');
  const [externalConnections, setExternalConnections] = useState([]);
  const [externalSource, setExternalSource] = useState(
    () => initialDraft?.externalSource || { connection_id: '', table_ref: null },
  );

  const externalSourceRef = useRef(externalSource);
  externalSourceRef.current = externalSource;
  const [externalSourceOptions, setExternalSourceOptions] = useState({ tables: [], views: [] });
  const [externalColumns, setExternalColumns] = useState(() => initialDraft?.externalColumns || []);
  const externalColumnsRef = useRef(externalColumns);
  externalColumnsRef.current = externalColumns;
  const [externalLocation, setExternalLocation] = useState(() => initialDraft?.externalLocation || '');
  const externalLocationRef = useRef(externalLocation);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [externalLoading, setExternalLoading] = useState({ tables: false, schema: false });
  const [externalError, setExternalError] = useState('');
  const [externalNotice, setExternalNotice] = useState('');
  const [discoveryAttempt, setDiscoveryAttempt] = useState(0);
  // Restored drafts always revalidate before they can be submitted.
  const [verifiedSource, setVerifiedSource] = useState('');
  const selectedConnection = externalConnections.find(connection => connection.id === externalSource.connection_id);
  const connectionRevisionKey = selectedConnection
    ? JSON.stringify([selectedConnection.operation_id, selectedConnection.updated_at]) : '';
  const connectionLocation = externalConnectionLocation(selectedConnection);
  const selectedSourceKey = sourceReferenceKey(externalSource.table_ref);
  const discoveryKey = JSON.stringify([companyName, externalSource.connection_id, connectionRevisionKey, selectedSourceKey]);
  const sourceAvailable = selectedConnection && !selectedConnection.deleting && !selectedConnection.creating;
  const externalReady = Boolean(sourceAvailable && selectedSourceKey && verifiedSource === discoveryKey
    && !externalLoading.tables && !externalLoading.schema && !externalError);

  useEffect(() => {
    if (onDraftChange) {
      onDraftChange({ activeStep, tableData, selectedTemplateId, storageMode, externalSource, externalColumns, externalLocation });
    }
  }, [onDraftChange, activeStep, tableData, selectedTemplateId, storageMode, externalSource, externalColumns, externalLocation]);
  const steps = useMemo(
    () => (storageMode === 'external' ? EXTERNAL_STEPS : (tableData.tableType === 'raw' ? RAW_STEPS : STANDARD_STEPS)),
    [storageMode, tableData.tableType]
  );
  const filteredTemplates = useMemo(
    () => templates.filter(t => t.tableType === tableData.tableType),
    [templates, tableData.tableType]
  );

  const cleanString = useCallback((str) => {
    return str.trim();
  }, []);

  const handleStorageModeChange = useCallback((_, nextMode) => {
    if (!nextMode || nextMode === storageMode) return;
    setStorageMode(nextMode);
    setActiveStep(0);
    setError('');
    setExternalError('');
    setSelectedTemplateId('');
    setTableData(prev => freshInitialTableData({
      name: prev.name,
      description: prev.description,
    }));
    setExternalSource({ connection_id: '', table_ref: null });
    setExternalSourceOptions({ tables: [], views: [] });
    setExternalColumns([]);
    setExternalLoading({ tables: false, schema: false });
    setVerifiedSource('');
    setExternalLocation('');
    externalLocationRef.current = '';
    setExternalNotice('');
  }, [storageMode]);

  const updateExternalColumn = useCallback((remote, patch) => {
    setExternalColumns(prev => prev.map(column => (
      column.remote === remote ? { ...column, ...patch } : column
    )));
  }, []);

  const includedExternalColumns = useMemo(
    () => externalColumns.filter(column => column.include && column.mappable !== false),
    [externalColumns]
  );

  const externalSourceChoices = useMemo(() => ([
    ...externalSourceOptions.tables.map(value => ({ value, key: sourceReferenceKey(value), kind: 'Table' })),
    ...externalSourceOptions.views.map(value => ({ value, key: sourceReferenceKey(value), kind: 'View' })),
  ]), [externalSourceOptions]);

  const cleanNativeTableDocument = useCallback((data) => {
    const metricSchema = {};
    Object.entries(normalizeMetricUpdateBehaviors(data.metricColumns?.schema)).forEach(([name, definition]) => {
      if (Object.prototype.hasOwnProperty.call(definition, 'type')) {
        metricSchema[name] = withScopedFlags(definition);
        return;
      }
      metricSchema[name] = {};
      Object.entries(definition).forEach(([childName, child]) => {
        metricSchema[name][childName] = withScopedFlags(child);
      });
    });

    // The classes drafted on a Text detail column in the Classes step become the
    // table's classification map: {column: {classes: {class: [aliases]}, threshold}}.
    const classification = {};
    (data.detailColumns || []).forEach((column) => {
      if (!column.classification) return;
      const classes = {};
      column.classification.classes.forEach((entry) => {
        classes[entry.name.trim()] = entry.aliases;
      });
      classification[column.name.trim()] = { classes, threshold: Number(column.classification.threshold) };
    });

    return {
      ...data,
      name: data.name.trim(),
      identifier: data.identifier ? { ...data.identifier, name: data.identifier.name.trim() } : data.identifier,
      detailColumns: (data.detailColumns || []).map((drafted) => {
        const column = withScopedFlags({ ...drafted, name: drafted.name.trim() });
        // The drafted classes are sent as the table's `classification` above.
        delete column.classification;
        return column;
      }),
      // Attribute pickers select category identifiers; send their readable names,
      // just like the manually named columns, for the backend to normalize.
      attributeColumns: (data.attributeColumns || []).map(column => ({ ...column, name: formatText(column.name) })),
      tableAttributeColumns: (data.tableAttributeColumns || []).map(column => ({ ...column, name: formatText(column.name) })),
      metricColumns: data.metricColumns ? { ...data.metricColumns, schema: metricSchema } : data.metricColumns,
      classification,
    };
  }, []);

  const validateCurrentStep = useCallback(() => {
    setError('');
    const stepLabel = steps[activeStep];
    if (storageMode === 'external') {
      switch (stepLabel) {
        case 'Basic Information':
          {
            const nameError = tableNameError(tableData.name);
            if (nameError) {
              setError(nameError);
              return false;
            }
          }
          if (!tableData.description.trim()) {
            setError('Table description is required');
            return false;
          }
          break;
        case 'Source':
          if (!externalSource.connection_id) {
            setError('Choose a source connection');
            return false;
          }
          if (!externalSource.table_ref) {
            setError('Choose a source table or view');
            return false;
          }
          if (!externalReady) {
            setError('Wait for source discovery to finish successfully');
            return false;
          }
          break;
        case 'Columns': {
          if (!externalReady) {
            setError('Refresh the source schema before continuing');
            return false;
          }
          if (includedExternalColumns.length < 2) {
            setError(RAW_COLUMNS_MESSAGE);
            return false;
          }
          const missingName = includedExternalColumns.find(column => !column.name?.trim());
          if (missingName) {
            setError(`Decode name is required for ${missingName.remote}`);
            return false;
          }
          const invalidType = includedExternalColumns.find(
            column => !decodeTypesFor(column.proposed_type).includes(column.type));
          if (invalidType) {
            setError(`Choose a valid Decode type for ${invalidType.remote}`);
            return false;
          }
          const nameError = tableCreationNamesError({ detailColumns: includedExternalColumns });
          if (nameError) {
            setError(nameError);
            return false;
          }
          break;
        }
        default:
          break;
      }
      return true;
    }
    const isRaw = tableData.tableType === 'raw';
    switch (stepLabel) {
      case 'Basic Information':
        {
          const nameError = tableNameError(tableData.name);
          if (nameError) {
            setError(nameError);
            return false;
          }
        }
        if (!tableData.description.trim()) {
          setError('Table description is required');
          return false;
        }
        break;
      case 'Column Definition':
      case 'Columns':
        {
          const nameError = tableCreationNamesError({ ...cleanNativeTableDocument(tableData), metricColumns: null });
          if (nameError) {
            setError(nameError);
            return false;
          }
        }
        if (isRaw) {
          if ((tableData.detailColumns || []).length < 2) {
            setError(RAW_COLUMNS_MESSAGE);
            return false;
          }
          break;
        }
        if (!tableData.identifier.name.trim()) {
          setError('Row identifier column is required');
          return false;
        }
        if ((tableData.identifier.type || 'Text') !== 'Text') {
          setError('The row identifier must be of type Text (identifiers are stored as text everywhere).');
          return false;
        }
        break;
      case 'Metrics Columns':
        {
          const nameError = tableCreationNamesError(cleanNativeTableDocument(tableData));
          if (nameError) {
            setError(nameError);
            return false;
          }
        }
        if (Object.keys(tableData.metricColumns?.schema || {}).length === 0) {
          setError(NO_METRICS_MESSAGE);
          return false;
        }
        break;
      case 'Mandatory Columns': {
        const details = tableData.detailColumns || [];
        const anyDetailMandatory = details.some(
          (column) => column.mandatory_per_file === true || column.mandatory_per_unit === true,
        );
        if (!isRaw && details.length > 0 && !anyDetailMandatory) {
          setError(MANDATORY_DETAIL_MESSAGE);
          return false;
        }
        break;
      }
      case 'Classes': {
        // A class list becomes a map keyed by class name, so two classes of one
        // column may not share a name.
        for (const column of tableData.detailColumns || []) {
          const names = (column.classification?.classes || []).map((entry) => entry.name.trim());
          const repeated = names.find((name, index) => name && names.indexOf(name) !== index);
          if (repeated) {
            setError(`Two classes of ${column.name.trim()} are both named ${repeated}.`);
            return false;
          }
        }
        const classesError = classificationError(cleanNativeTableDocument(tableData));
        if (classesError) {
          setError(classesError);
          return false;
        }
        break;
      }
      default:
        break;
    }
    return true;
  }, [
    activeStep,
    steps,
    storageMode,
    tableData,
    externalSource,
    includedExternalColumns,
    externalReady,
    cleanNativeTableDocument,
  ]);

  const handleNext = useCallback(() => {
    if (validateCurrentStep()) {
      setActiveStep(prevStep => prevStep + 1);
    }
  }, [validateCurrentStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prevStep => prevStep - 1);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setError('');

      if (storageMode === 'external') {
        if (!externalReady) {
          setError('Refresh the source schema before creating the table');
          return;
        }
        if (includedExternalColumns.length < 2) {
          setError(RAW_COLUMNS_MESSAGE);
          return;
        }
        const nameError = tableCreationNamesError({ detailColumns: includedExternalColumns });
        if (nameError) {
          setError(nameError);
          return;
        }
        const mappedColumns = includedExternalColumns.map((column) => ({
          name: cleanString(column.name.trim()),
          type: column.type,
          remote: column.remote,
          ...(column.index ? { index: true } : {}),
        }));
        const display_columns = mappedColumns.reduce((acc, column) => {
          acc[column.name] = true;
          return acc;
        }, {});
        // Only the columns this table actually maps are pinned. Pinning every
        // fetched column made Validate Schema's "new columns" list permanently
        // empty for the ones left unmapped at creation, and its refresh step kept
        // them pinned forever — so a column skipped here could never be adopted
        // later, and the only way back was deleting and recreating the table.
        const pinned_schema = includedExternalColumns.reduce((acc, column) => {
          if (column.remote) acc[column.remote] = column.native_type;
          return acc;
        }, {});

        const table_doc = {
          name: tableData.name.trim(),
          description: tableData.description.trim(),
          tableType: 'raw',
          storage: 'external',
          connection_id: externalSource.connection_id,
          source: { table_ref: externalSource.table_ref, connection_revision: externalSource.connection_revision },
          detailColumns: mappedColumns,
          attributeColumns: [],
          tableAttributeColumns: [],
          pinned_schema,
          display_columns,
          display_order: {
            attribute: [],
            detail: mappedColumns.map(column => column.name),
            metric: [],
          },
        };

        const response = await authenticatedFetch(functionEndpoint('create_table'), {
          method: 'POST',
          body: JSON.stringify({ table_doc })
        });

        if (!response.ok) {
          setError(await createTableRefusalMessage(response));
          return;
        }

        const created = await response.json();
        onTableCreated(created);
        return;
      }

      if (tableData.tableType !== 'raw' && Object.keys(tableData.metricColumns?.schema || {}).length === 0) {
        setError(NO_METRICS_MESSAGE);
        return;
      }

      const cleanedTableData = cleanNativeTableDocument(tableData);
      if (cleanedTableData.tableType === 'raw') {
        if (cleanedTableData.detailColumns.length < 2) {
          setError(RAW_COLUMNS_MESSAGE);
          return;
        }
        delete cleanedTableData.identifier;
        delete cleanedTableData.metricColumns;
        delete cleanedTableData.timeSeriesConfig;
      }

      const nameError = tableCreationNamesError(cleanedTableData);
      if (nameError) {
        setError(nameError);
        return;
      }

      // Initialize display_columns with all attribute and metadata columns set to true
      const display_columns = {};
      [...cleanedTableData.detailColumns, ...cleanedTableData.attributeColumns].forEach(col => {
        display_columns[col.name] = true;
      });
      // Table attribute columns are hidden by default
      (cleanedTableData.tableAttributeColumns || []).forEach(col => {
        display_columns[col.name] = false;
      });

      const table_doc = {
        ...cleanedTableData,
        display_columns,
        createdAt: new Date().toISOString(),
      };

      // Create table via API
      const response = await authenticatedFetch(functionEndpoint('create_table'), {
        method: 'POST',
        body: JSON.stringify({ table_doc })
      });

      if (!response.ok) {
        setError(await createTableRefusalMessage(response));
        return;
      }

      const created = await response.json();
      onTableCreated(created);
    } catch (err) {
      // A request that never reached create_table, or an answer that could not be read.
      console.error('Error creating table:', err);
      setError(TABLE_NOT_CREATED_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    storageMode,
    includedExternalColumns,
    externalReady,
    externalSource,
    tableData,
    onTableCreated,
    cleanNativeTableDocument,
    cleanString,
  ]);

  const handleBasicInfoChange = useCallback((field, value) => {
    setTableData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleColumnDefinitionChange = useCallback((columnType, columns) => {
    setTableData(prev => ({
      ...prev,
      [columnType]: columns
    }));
  }, []);

  const handleValueColumnsChange = useCallback((metricColumns) => {
    setTableData(prev => ({
      ...prev,
      metricColumns
    }));
  }, []);

  // One box of the Mandatory Columns step: `row` names a detail column by its
  // index or a metric leaf by its path, `flag` is mandatory_per_file or
  // mandatory_per_unit.
  const handleMandatoryFlagChange = useCallback((row, flag, checked) => {
    setTableData((prev) => {
      if (row.detailIndex !== undefined) {
        const detailColumns = [...prev.detailColumns];
        detailColumns[row.detailIndex] = { ...detailColumns[row.detailIndex], [flag]: checked };
        return { ...prev, detailColumns };
      }
      const [name, childName] = row.metricPath;
      const schema = { ...prev.metricColumns.schema };
      if (childName === undefined) {
        schema[name] = { ...schema[name], [flag]: checked };
      } else {
        schema[name] = { ...schema[name], [childName]: { ...schema[name][childName], [flag]: checked } };
      }
      return { ...prev, metricColumns: { ...prev.metricColumns, schema } };
    });
  }, []);

  // The classes drafted on one Text detail column: {threshold, classes: [{name,
  // aliases, aliasInput}]}, `aliases` the class's aliases, added one at a time as
  // Table Settings adds them, and `aliasInput` the alias being typed for it; null
  // removes them.
  const handleClassificationChange = useCallback((detailIndex, drafted) => {
    setTableData((prev) => {
      const detailColumns = [...prev.detailColumns];
      const column = { ...detailColumns[detailIndex] };
      if (drafted) {
        column.classification = drafted;
      } else {
        delete column.classification;
      }
      detailColumns[detailIndex] = column;
      return { ...prev, detailColumns };
    });
  }, []);

  const applyTemplate = useCallback((template) => {
    if (!template) return;
    const templateType = template.tableType;
    let editableMetrics;
    try {
      editableMetrics = metricNamesForEditing(template.metricColumns?.schema);
    } catch (error) {
      setError(error.message);
      return;
    }
    setTableData(prev => ({
      ...prev,
      // Do not touch name/description unless you want to; keep user's entries
      // Identifiers are always Text (stored as strings everywhere).
      identifier: templateType === 'raw' ? { name: '', type: 'Text' } : {
        ...(template.identifier || prev.identifier),
        name: formatText((template.identifier || prev.identifier).name), type: 'Text',
      },
      tableType: templateType,
      timeSeriesConfig: templateType === 'timeSeries' ? (template.timeSeriesConfig || prev.timeSeriesConfig) : undefined,
      detailColumns: template.detailColumns
        ? template.detailColumns.map(column => ({ ...column, name: formatText(column.name) }))
        : prev.detailColumns,
      metricColumns: templateType === 'raw' ? null : {
        ...template.metricColumns,
        schema: normalizeMetricUpdateBehaviors(editableMetrics),
      },
    }));
  }, []);

  // Fetch table templates (global collection)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const tplRef = collection(db, 'table_templates');
        const snapshot = await getDocs(tplRef);
        const tpl = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setTemplates(tpl);
      } catch (e) {
        console.error('Failed to load table templates', e);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!companyName) return undefined;
    const ref = collection(db, `users/${companyName}/external_connections`);
    return onSnapshot(ref, (snapshot) => {
      const rows = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
      rows.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      setExternalConnections(rows);
      setConnectionsLoaded(true);
    }, (err) => {
      console.error('Failed to load source connections', err);
      setExternalError('Failed to load source connections');
    });
  }, [companyName, discoveryAttempt]);

  useEffect(() => {
    if (storageMode !== 'external' || !externalSource.connection_id || !connectionsLoaded) return undefined;
    setVerifiedSource('');
    if (!sourceAvailable) {
      setExternalLoading(previous => ({ ...previous, tables: false }));
      setExternalError('The selected connection is unavailable. Choose another connection.');
      return undefined;
    }
    const locationChanged = externalLocationRef.current && externalLocationRef.current !== connectionLocation;
    externalLocationRef.current = connectionLocation;
    setExternalLocation(connectionLocation);
    if (locationChanged) {
      setExternalSource(previous => ({ connection_id: previous.connection_id, table_ref: null }));
      setExternalColumns([]);
      setActiveStep(1);
      setExternalNotice('The source location changed. Choose a source and review its columns again.');
    }
    let cancelled = false;
    const revision = JSON.parse(connectionRevisionKey);
    const loadTables = async () => {
      setExternalLoading(previous => ({ ...previous, tables: true }));
      setExternalError('');
      setExternalSourceOptions({ tables: [], views: [] });
      try {
        const result = await browseExternalSource({
          action: 'list_tables', connection_id: externalSource.connection_id,
          connection_revision: { operation_id: revision[0], updated_at: revision[1] },
        });
        if (cancelled) return;
        setExternalSourceOptions({ tables: result.tables, views: result.views });
        const selected = sourceReferenceKey(externalSourceRef.current.table_ref);
        const stillListed = [...result.tables, ...result.views].some(reference => sourceReferenceKey(reference) === selected);
        if (selected && !stillListed) {
          setExternalSource(previous => ({ connection_id: previous.connection_id, table_ref: null }));
          setExternalColumns([]);
          setActiveStep(1);
          setExternalNotice('The selected table or view is no longer available. Choose another source.');
        }
      } catch (err) {
        if (!cancelled) setExternalError(err.message || 'Failed to list source tables');
      } finally {
        if (!cancelled) setExternalLoading(previous => ({ ...previous, tables: false }));
      }
    };
    loadTables();
    return () => { cancelled = true; };
  }, [companyName, storageMode, externalSource.connection_id, connectionsLoaded, sourceAvailable, connectionLocation, connectionRevisionKey, discoveryAttempt]);

  useEffect(() => {
    if (storageMode !== 'external' || !sourceAvailable || !selectedSourceKey
        || externalLocation !== connectionLocation) {
      setExternalLoading(previous => ({ ...previous, schema: false }));
      setVerifiedSource('');
      return undefined;
    }
    let cancelled = false;
    const revision = JSON.parse(connectionRevisionKey);
    const [catalog, schema, name] = JSON.parse(selectedSourceKey);
    const loadSchema = async () => {
      setVerifiedSource('');
      setExternalLoading(previous => ({ ...previous, schema: true }));
      try {
        const result = await browseExternalSource({
          action: 'fetch_schema', connection_id: externalSource.connection_id,
          table_ref: { catalog, schema, name },
          connection_revision: { operation_id: revision[0], updated_at: revision[1] },
        });
        if (cancelled) return;
        const previousColumns = externalColumnsRef.current;
        const changed = previousColumns.length > 0
          && externalSchemaSignature(previousColumns) !== externalSchemaSignature(result.columns);
        const previousByRemote = new Map(previousColumns.map(column => [column.remote, column]));
        setExternalColumns(result.columns.map(column => {
          const previous = changed ? null : previousByRemote.get(column.remote);
          return {
            ...column,
            include: previous ? previous.include : !!column.mappable,
            name: previous ? previous.name : formatText(column.proposed_name || column.remote),
            type: previous ? previous.type : (column.proposed_type || 'Text'),
            index: previous ? previous.index : false,
          };
        }));
        if (changed) {
          setActiveStep(2);
          setExternalNotice('The source schema changed, so your column selections need to be reviewed again.');
        }
        setExternalSource(previous => ({ ...previous, connection_revision: result.connection_revision }));
        setVerifiedSource(discoveryKey);
      } catch (err) {
        if (!cancelled) setExternalError(err.message || 'Failed to fetch source schema');
      } finally {
        if (!cancelled) setExternalLoading(previous => ({ ...previous, schema: false }));
      }
    };
    loadSchema();
    return () => { cancelled = true; };
  }, [storageMode, sourceAvailable, externalSource.connection_id, selectedSourceKey, externalLocation,
    connectionLocation, connectionRevisionKey, discoveryKey, discoveryAttempt]);

  const renderStepContent = useCallback((step) => {
    const storageFork = (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Table storage</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={storageMode}
          onChange={handleStorageModeChange}
        >
          <ToggleButton value="native">Upload-based table</ToggleButton>
          <ToggleButton value="external">Connect an external source</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );

    if (storageMode === 'external') {
      switch (step) {
        case 'Basic Information':
          return (
            <Box sx={{ p: 2 }}>
              {storageFork}
              <TextField
                fullWidth
                label="Table Name"
                value={tableData.name}
                onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={tableData.description}
                onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                margin="normal"
                required
                multiline
                rows={3}
              />
            </Box>
          );
        case 'Source':
          return (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <FormControl fullWidth>
                    <InputLabel>Source Connection</InputLabel>
                    <Select
                      label="Source Connection"
                      value={externalSource.connection_id}
                      onChange={(e) => {
                        setExternalSource({ connection_id: e.target.value, table_ref: null });
                        setExternalColumns([]);
                        setVerifiedSource('');
                        setExternalNotice('');
                        externalLocationRef.current = '';
                        setExternalLocation('');
                      }}
                    >
                      {externalConnections.map((connection) => (
                        <MenuItem key={connection.id} value={connection.id} disabled={connection.deleting || connection.creating}>
                          {connection.name || connection.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {onManageConnections && (
                    <Button onClick={onManageConnections} sx={{ whiteSpace: 'nowrap' }}>
                      Manage
                    </Button>
                  )}
                </Box>
                {externalConnections.length === 0 && (
                  <Alert severity="info">
                    Create a source connection before adding an external table.
                  </Alert>
                )}
                <FormControl fullWidth disabled={!externalSource.connection_id || externalLoading.tables}>
                  <InputLabel>Source table or view</InputLabel>
                  <Select
                    label="Source table or view"
                    value={selectedSourceKey}
                    onChange={(e) => {
                      const selected = externalSourceChoices.find(choice => choice.key === e.target.value);
                      setExternalSource(previous => ({ connection_id: previous.connection_id, table_ref: selected.value }));
                      setExternalColumns([]);
                      setVerifiedSource('');
                      setExternalNotice('');
                      setExternalError('');
                    }}
                    endAdornment={externalLoading.tables ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null}
                  >
                    {selectedSourceKey && !externalSourceChoices.some(choice => choice.key === selectedSourceKey) && (
                      <MenuItem value={selectedSourceKey} disabled>{sourceReferenceLabel(externalSource.table_ref)}</MenuItem>
                    )}
                    {externalSourceChoices.map((choice) => (
                      <MenuItem key={`${choice.kind}-${choice.key}`} value={choice.key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{sourceReferenceLabel(choice.value)}</Typography>
                          <Chip label={choice.kind} size="small" variant="outlined" />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          );
        case 'Columns':
          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose source columns to expose in Decode. Date-typed columns can be range-filtered when querying.
              </Typography>
              {externalLoading.schema ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading source schema...</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Include</TableCell>
                      <TableCell>Remote column</TableCell>
                      <TableCell>Decode name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Searchable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {externalColumns.map((column) => {
                      const included = !!column.include && column.mappable !== false;
                      return (
                        <TableRow key={column.remote}>
                          <TableCell>
                            <Checkbox
                              checked={included}
                              disabled={column.mappable === false}
                              onChange={(e) => updateExternalColumn(column.remote, {
                                include: e.target.checked,
                                ...(e.target.checked ? {} : { index: false }),
                              })}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{column.remote}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {column.native_type || 'Unknown'}
                              {column.mappable === false ? ' - not mappable' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={column.name || ''}
                              onChange={(e) => updateExternalColumn(column.remote, { name: e.target.value })}
                              disabled={!included}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth disabled={!included}>
                              <Select
                                value={column.type || 'Text'}
                                onChange={(e) => updateExternalColumn(column.remote, {
                                  type: e.target.value,
                                  ...(e.target.value === 'Text' ? {} : { index: false }),
                                })}
                              >
                                {decodeTypesFor(column.proposed_type).map((type) => (
                                  <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!column.index}
                                  disabled={!included || column.type !== 'Text'}
                                  onChange={(e) => updateExternalColumn(column.remote, { index: e.target.checked })}
                                />
                              }
                              label=""
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          );
        case 'Review': {
          const connection = externalConnections.find(c => c.id === externalSource.connection_id);
          const searchable = includedExternalColumns.filter(column => column.index);
          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>External Table Summary</Typography>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Source</Typography>
                  <Typography variant="body2">Connection: {connection?.name || externalSource.connection_id}</Typography>
                  <Typography variant="body2">Table/view: {sourceReferenceLabel(externalSource.table_ref)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Mapped columns</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {includedExternalColumns.map((column) => (
                      <Chip
                        key={column.remote}
                        label={`${cleanString(column.name)} <- ${column.remote} (${column.type})`}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Searchable columns</Typography>
                  <Typography variant="body2">
                    {searchable.length ? searchable.map(column => cleanString(column.name)).join(', ') : 'None selected'}
                  </Typography>
                </Paper>
              </Stack>
            </Box>
          );
        }
        default:
          return null;
      }
    }

    switch (step) {
      case 'Basic Information':
        return (
          <Box sx={{ p: 2 }}>
            {storageFork}
            <FormControl fullWidth margin="normal">
              <InputLabel>Table Template</InputLabel>
              <Select
                value={selectedTemplateId}
                label="Table Template"
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedTemplateId(id);
                  const tpl = filteredTemplates.find(t => t.id === id);
                  applyTemplate(tpl);
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {filteredTemplates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name || t.id}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Table Type</InputLabel>
              <Select
                value={tableData.tableType}
                label="Table Type"
                onChange={(e) => {
                  const newType = e.target.value;
                  setTableData(prev => ({
                    ...prev,
                    tableType: newType,
                    identifier: newType === 'raw' ? { name: '', type: 'Text' } : (prev.identifier || { name: '', type: 'Text' }),
                    timeSeriesConfig: newType === 'timeSeries' ? { period: prev.timeSeriesConfig?.period || DEFAULT_PERIOD } : undefined,
                    metricColumns: newType === 'raw' ? null : { schema: {} },
                  }));
                  setSelectedTemplateId('');
                  setActiveStep(0);
                }}
              >
                <MenuItem value="timeSeries">Time Series (Periodic Data)</MenuItem>
                <MenuItem value="raw">Raw (Append-Only Rows)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {tableData.tableType === 'raw'
                ? 'Raw tables append rows exactly as uploaded. Nothing is merged — ideal for transaction logs, event data, and row-level dumps.'
                : 'Time-series tables store data indexed by a time period (daily, monthly, quarterly, or yearly).'}
            </Typography>

            {/* Period Selection - only shown for time series tables */}
            {tableData.tableType === 'timeSeries' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={tableData.timeSeriesConfig?.period || DEFAULT_PERIOD}
                  label="Time Period"
                  onChange={(e) => {
                    const newPeriod = e.target.value;
                    setTableData(prev => ({
                      ...prev,
                      timeSeriesConfig: { period: newPeriod }
                    }));
                  }}
                >
                  {getPeriodOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} ({option.example})
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                  {PERIOD_CONFIG[tableData.timeSeriesConfig?.period || DEFAULT_PERIOD]?.description}
                </Typography>
              </FormControl>
            )}

            <Typography variant="caption" color="textSecondary">
              {tableData.tableType === 'raw'
                ? 'Templates include the table\'s columns. You still need to set attribute columns.'
                : 'Templates include the row identifier, detail columns and metric columns. You still need to set attribute columns.'}
            </Typography>
            <TextField
              fullWidth
              label="Table Name"
              value={tableData.name}
              onChange={(e) => handleBasicInfoChange('name', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={tableData.description}
              onChange={(e) => handleBasicInfoChange('description', e.target.value)}
              margin="normal"
              required
              multiline
              rows={3}
            />
          </Box>
        );
      case 'Column Definition':
      case 'Columns':
        return (
          <ColumnDefinition
            identifier={tableData.identifier}
            attributeColumns={tableData.attributeColumns}
            tableAttributeColumns={tableData.tableAttributeColumns}
            detailColumns={tableData.detailColumns}
            attributeCategories={attributeCategories}
            categoryValues={categoryValues}
            tableType={tableData.tableType}
            onChange={handleColumnDefinitionChange}
          />
        );
      case 'Metrics Columns':
        return (
          <ValueColumnBuilder
            key="value-builder"
            value={tableData.metricColumns}
            periodType={tableData.timeSeriesConfig?.period}
            onChange={handleValueColumnsChange}
          />
        );
      case 'Mandatory Columns': {
        // One row per detail column (a raw table's data column) and metric leaf,
        // a nested metric by its full dotted path. Attribute columns keep their
        // upload-tag switch; the table's constants are not offered.
        const isRaw = tableData.tableType === 'raw';
        const rows = (tableData.detailColumns || []).map((column, index) => ({
          key: `detail-${index}`, label: column.name, definition: column, detailIndex: index,
        }));
        Object.entries(tableData.metricColumns?.schema || {}).forEach(([name, definition]) => {
          if (Object.prototype.hasOwnProperty.call(definition, 'type')) {
            rows.push({ key: `metric-${name}`, label: name, definition, metricPath: [name] });
            return;
          }
          Object.entries(definition).forEach(([childName, child]) => {
            rows.push({
              key: `metric-${name}.${childName}`, label: `${name}.${childName}`, definition: child,
              metricPath: [name, childName],
            });
          });
        });
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isRaw ? RAW_MANDATORY_EXPLANATION : TIME_SERIES_MANDATORY_EXPLANATION}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Column</TableCell>
                  <TableCell>Mandatory Per File</TableCell>
                  <TableCell>Mandatory Per Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      <Checkbox
                        checked={row.definition.mandatory_per_file === true}
                        onChange={(e) => handleMandatoryFlagChange(row, 'mandatory_per_file', e.target.checked)}
                        inputProps={{ 'aria-label': `${row.label} mandatory per file` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={row.definition.mandatory_per_unit === true}
                        onChange={(e) => handleMandatoryFlagChange(row, 'mandatory_per_unit', e.target.checked)}
                        inputProps={{ 'aria-label': `${row.label} mandatory per unit` }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      }
      case 'Classes': {
        const textColumns = (tableData.detailColumns || [])
          .map((column, index) => ({ column, index }))
          .filter(({ column }) => (column.type || 'Text') === 'Text');
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Optionally sort a Text detail column's values into classes.
            </Typography>
            {textColumns.map(({ column, index }) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(column.classification)}
                      onChange={(e) => handleClassificationChange(index, e.target.checked
                        ? { threshold: '', classes: [{ name: '', aliases: [], aliasInput: '' }] }
                        : null)}
                    />
                  )}
                  label={`Classify ${column.name}`}
                />
                {column.classification && (
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Similarity threshold (above 0, at most 1)"
                      value={column.classification.threshold}
                      onChange={(e) => handleClassificationChange(index, {
                        ...column.classification, threshold: e.target.value,
                      })}
                      inputProps={{ min: 0, max: 1, step: 0.05 }}
                      sx={{ maxWidth: 280 }}
                    />
                    {column.classification.classes.map((entry, classIndex) => {
                      const changeClass = (patch) => handleClassificationChange(index, {
                        ...column.classification,
                        classes: column.classification.classes.map((existing, position) => (
                          position === classIndex ? { ...existing, ...patch } : existing
                        )),
                      });
                      const typedAlias = entry.aliasInput.trim();
                      return (
                        <Box key={classIndex}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              label="Class"
                              value={entry.name}
                              onChange={(e) => changeClass({ name: e.target.value })}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Remove the class ${entry.name || classIndex + 1}`}
                              onClick={() => handleClassificationChange(index, {
                                ...column.classification,
                                classes: column.classification.classes.filter((_, position) => position !== classIndex),
                              })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ my: 0.5 }}>
                            {entry.aliases.map((alias, aliasIndex) => (
                              <Chip
                                key={`${alias}-${aliasIndex}`}
                                label={alias}
                                size="small"
                                onDelete={() => changeClass({
                                  aliases: entry.aliases.filter((_, position) => position !== aliasIndex),
                                })}
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="Add alias"
                              value={entry.aliasInput}
                              onChange={(e) => changeClass({ aliasInput: e.target.value })}
                              sx={{ flexGrow: 1, maxWidth: 400 }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`Add an alias to the class ${entry.name || classIndex + 1}`}
                              disabled={!typedAlias}
                              onClick={() => changeClass({ aliases: [...entry.aliases, typedAlias], aliasInput: '' })}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleClassificationChange(index, {
                          ...column.classification,
                          classes: [...column.classification.classes, { name: '', aliases: [], aliasInput: '' }],
                        })}
                      >
                        Add Class
                      </Button>
                    </Box>
                  </Stack>
                )}
              </Paper>
            ))}
          </Box>
        );
      }
      case 'Review':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Table Schema Preview
            </Typography>
            {tableData.tableType === 'timeSeries' && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Reporting period: <strong>{PERIOD_CONFIG[tableData.timeSeriesConfig?.period || DEFAULT_PERIOD]?.label}</strong>
              </Typography>
            )}
            <TableSchemaPreview schema={tableData} />
          </Box>
        );
      default:
        return null;
    }
  }, [
    storageMode,
    handleStorageModeChange,
    tableData,
    handleBasicInfoChange,
    handleColumnDefinitionChange,
    handleValueColumnsChange,
    handleMandatoryFlagChange,
    handleClassificationChange,
    applyTemplate,
    attributeCategories,
    categoryValues,
    selectedTemplateId,
    filteredTemplates,
    externalConnections,
    externalSource,
    externalSourceChoices,
    selectedSourceKey,
    externalLoading,
    externalColumns,
    updateExternalColumn,
    includedExternalColumns,
    onManageConnections,
    cleanString,
  ]);

  return (
    <Paper sx={{ p: 3 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {storageMode === 'external' && externalNotice && <Alert severity="info" sx={{ mb: 2 }}>{externalNotice}</Alert>}
      {storageMode === 'external' && externalError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" disabled={externalLoading.tables || externalLoading.schema}
            onClick={() => { setExternalError(''); setDiscoveryAttempt(attempt => attempt + 1); }}>Retry discovery</Button>
        }>{externalError}</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {renderStepContent(steps[activeStep])}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || (storageMode === 'external' && !externalReady)}
          >
            {isSubmitting ? 'Creating...' : 'Create Table'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default CreateTableForm;
